import { create } from 'zustand';

/**
 * Settings Store - User preferences and settings state
 * Manages account settings, notifications, privacy, and app preferences
 */
export const useSettingsStore = create((set, get) => ({
  // Account settings
  account: {
    email: 'jordan@example.com',
    connectedAccounts: {
      google: true,
      apple: false,
      facebook: false,
    },
  },

  // Notification settings
  notifications: {
    pushEnabled: true,
    eventReminders: true,
    friendActivity: true,
    messages: true,
    promotional: false,
  },

  // Privacy settings
  privacy: {
    profileVisibility: 'public', // 'public', 'friends', 'private'
    activityStatus: true,
    locationSharing: true,
    locationPrecision: 'precise', // 'precise', 'approximate'
    blockedUsersCount: 0,
  },

  // App preferences
  preferences: {
    distanceUnit: 'miles', // 'miles', 'km'
    defaultSport: 'all',
    language: 'en',
    theme: 'dark', // 'dark', 'light', 'system'
  },

  // App info
  appInfo: {
    version: '3.0.0',
    buildNumber: '2023.1216.1',
  },

  // UI state
  isLoading: false,
  error: null,

  // Notification actions
  setNotification: (key, value) => {
    set((state) => ({
      notifications: {
        ...state.notifications,
        [key]: value,
      },
    }));
  },

  setAllNotifications: (enabled) => {
    set((state) => ({
      notifications: {
        pushEnabled: enabled,
        eventReminders: enabled,
        friendActivity: enabled,
        messages: enabled,
        promotional: state.notifications.promotional, // Keep promotional separate
      },
    }));
  },

  // Privacy actions
  setPrivacy: (key, value) => {
    set((state) => ({
      privacy: {
        ...state.privacy,
        [key]: value,
      },
    }));
  },

  // Preference actions
  setPreference: (key, value) => {
    set((state) => ({
      preferences: {
        ...state.preferences,
        [key]: value,
      },
    }));

    // Apply theme change immediately
    if (key === 'theme') {
      get().applyTheme(value);
    }
  },

  // Theme application
  applyTheme: (theme) => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
      root.classList.remove('light');
    } else if (theme === 'light') {
      root.classList.add('light');
      root.classList.remove('dark');
    } else {
      // System preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
        root.classList.remove('light');
      } else {
        root.classList.add('light');
        root.classList.remove('dark');
      }
    }
  },

  // Account actions
  connectAccount: (provider) => {
    set((state) => ({
      account: {
        ...state.account,
        connectedAccounts: {
          ...state.connectedAccounts,
          [provider]: true,
        },
      },
    }));
  },

  disconnectAccount: (provider) => {
    set((state) => ({
      account: {
        ...state.account,
        connectedAccounts: {
          ...state.connectedAccounts,
          [provider]: false,
        },
      },
    }));
  },

  // Loading and error states
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  clearError: () => set({ error: null }),

  // Reset all settings to defaults
  resetToDefaults: () => {
    set({
      notifications: {
        pushEnabled: true,
        eventReminders: true,
        friendActivity: true,
        messages: true,
        promotional: false,
      },
      privacy: {
        profileVisibility: 'public',
        activityStatus: true,
        locationSharing: true,
        locationPrecision: 'precise',
        blockedUsersCount: 0,
      },
      preferences: {
        distanceUnit: 'miles',
        defaultSport: 'all',
        language: 'en',
        theme: 'dark',
      },
    });
  },
}));

export default useSettingsStore;
